<template>
  <div v-if="isActive">
    <slot></slot>
  </div>
  <div v-else-if="!isRoot && content && !hideFallback" class="feature-gate-fallback">
    <!-- Upsell Card for Inactive Feature (Matching Benefit Module Disabled style) -->
    <div class="feature-disabled-container mt-4">
      <div class="disabled-content">
        <!-- Optional Icon (Lock instead of toggle) -->
        <i class="fas fa-lock fa-3x primary-text mb-3" style="color: #64748b;"></i>
        
        <h4>This Feature is Locked</h4>
        <p class="mb-4">
          Contact our sales team to unlock and access advanced feature.
        </p>

        <button 
          type="button" 
          class="btn px-4 py-2"
          style="background-color: var(--primary-color, #10b981); border-color: var(--primary-color, #10b981); color: white;"
          @click="openModal"
        >
          View about this Feature
        </button>
      </div>
    </div>

    <!-- Upsell Modal -->
    <feature-upsell-modal
      :visible="isModalVisible"
      :featureKey="featureKey"
      context="page_direct_access"
      @close="closeModal"
    />
  </div>
  <div v-else-if="!hideFallback" class="feature-gate-fallback">
    <!-- Globally Disabled State -->
    <div class="feature-disabled-container mt-4">
      <div class="disabled-content">
        <i class="fas fa-toggle-off fa-3x primary-text mb-3"></i>
        <h4>This Feature is Disabled</h4>
        <p v-if="isRoot">
          The {{ formattedFeatureName }} feature is currently disabled for this corporate account. To enable it, please navigate to the Feature Flags menu in the Membership Extranet.
        </p>
        <p v-else>
          The {{ formattedFeatureName }} feature has been disabled by System Administration. Please contact System Owner to enable this feature.
        </p>
      </div>
    </div>
  </div>
  <div v-else class="feature-gate-hidden">
    <!-- Render nothing if hideFallback is true and feature is inactive (for things like buttons) -->
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import FeatureUpsellModal from './FeatureUpsellModal.vue';
import { useFeatureUpsell } from '@/composables/useFeatureUpsell';

export default {
  name: 'FeatureGate',
  components: {
    FeatureUpsellModal
  },
  props: {
    featureKey: {
      type: String,
      required: true
    },
    // Allows overriding the Vuex active state if the parent component evaluates it differently
    forceActive: {
      type: Boolean,
      default: null
    },
    hideFallback: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const store = useStore();
    const { getContent, isDismissed } = useFeatureUpsell();
    const isModalVisible = ref(false);

    const isActive = computed(() => {
      if (props.forceActive !== null) {
        return props.forceActive;
      }
      
      const featureFlagMap = {
        'marketing_module': store.getters['FeatureFlag/getMarketingModuleFlag'],
        'promotions_module': store.getters['FeatureFlag/getPromotionsModuleFlag'],
        'newsletter_module': store.getters['FeatureFlag/getNewsletterModuleFlag'],
        'point_control': store.getters['FeatureFlag/getPointControlFlag'],
        'voucher_gift': store.getters['FeatureFlag/getVoucherGiftFlag'],
        'transaction_report_module': store.getters['FeatureFlag/getTransactionReportModuleFlag'],
        'threshold_setting': store.getters['FeatureFlag/getThresholdSettingFlag'],
      };

      if (props.featureKey === 'voucher_gift') {
        const pointFlag = store.getters['FeatureFlag/getPointControlFlag'];
        if (typeof pointFlag === 'string' && pointFlag.toUpperCase() === 'NONACTIVE') {
          return false;
        }
      }

      if (featureFlagMap[props.featureKey] !== undefined) {
        const flagVal = featureFlagMap[props.featureKey];
        if (typeof flagVal === 'string') {
          const upper = flagVal.toUpperCase();
          return upper === 'ACTIVE' || upper === 'DECENTRALIZE' || upper === 'CENTRALIZE';
        }
        return flagVal === true;
      }

      const flags = store.getters['FeatureFlag/flags'] || {};
      const val = flags[props.featureKey];
      if (typeof val === 'string') {
        const upper = val.toUpperCase();
        return upper === 'DECENTRALIZE' || upper === 'ACTIVE' || upper === 'CENTRALIZE';
      }
      return val === true;
    });

    const isRoot = computed(() => {
      const userLevel = store.getters['Auth/user']?.level || store.state.Auth?.currUser?.level;
      return userLevel === 0 || userLevel === '0' || userLevel === 'root';
    });

    const content = computed(() => getContent(props.featureKey));

    const openModal = () => {
      isModalVisible.value = true;
    };

    const closeModal = () => {
      isModalVisible.value = false;
    };

    watch(
      () => [isActive.value, content.value],
      ([active, currentContent]) => {
        if (!active && currentContent && !isDismissed(props.featureKey) && !props.hideFallback && !isModalVisible.value && !isRoot.value) {
          setTimeout(() => {
            openModal();
          }, 500);
        }
      },
      { immediate: true }
    );

    const formattedFeatureName = computed(() => {
      const rawContent = store.getters['feature-upsell/getContent'](props.featureKey);
      if (rawContent && rawContent.title) {
        return rawContent.title;
      }

      if (!props.featureKey) return 'Feature';
      const featureTitleMap = {
        'point_control': 'Point Control',
        'marketing_module': 'Marketing Module',
        'transaction_report_module': 'Transaction Report Module',
        'threshold_setting': 'Threshold Setting',
        'newsletter_module': 'Newsletter Module',
        'promotions_module': 'Promotions Module',
        'voucher_gift': 'Voucher Gift',
      };
      if (featureTitleMap[props.featureKey]) {
        return featureTitleMap[props.featureKey];
      }
      return props.featureKey.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    });

    return {
      isActive,
      content,
      isModalVisible,
      isRoot,
      formattedFeatureName,
      openModal,
      closeModal
    };
  }
}
</script>

<style scoped>
.feature-disabled-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  width: 100%;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px dashed #dee2e6;
  text-align: center;
}
.disabled-content {
  width: 100%;
  padding: 2rem;
}
.disabled-content h4 {
  font-weight: 600;
  color: #2c3e50;
}
.disabled-content p {
  color: #6c757d;
}
.primary-text {
  color: #ec008c; /* fallback if inline style removed */
}
</style>
